import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  useListRoutingRules,
  useCreateRoutingRule,
  useUpdateRoutingRule,
  useDeleteRoutingRule,
  useReorderRoutingRule,
  getListRoutingRulesQueryKey,
} from "@workspace/api-client-react";
import type { RoutingRule, TaskPriority } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Route, ShieldAlert } from "lucide-react";

// Phase 41 Plan 10 (Wave 7), Task 2 — TCFG-03 Settings CRUD for
// routing_rule, ORDERED + first-match-wins (41-CONTEXT.md hard constraint).
// Rows render in server-returned sortOrder ascending — that order IS the
// evaluation order Phase 42 will walk. Reordering happens ONLY through the
// dedicated /reorder endpoint (row-locked adjacent swap) — never a
// sortOrder field in the Edit dialog, matching UpdateRoutingRuleBody's
// shape (it deliberately has no sortOrder field server-side).
//
// No @dnd-kit (not a repo dependency) — ArrowUp/ArrowDown buttons per row,
// per the plan's explicit instruction (deferred to Phase 51 Queues).

const TASK_TYPES = ["complaint", "request", "maintenance", "assignment", "feedback_followup", "operational"] as const;
const PRIORITIES: TaskPriority[] = ["P1", "P2", "P3", "P4"];
const ALL_ROLES = ["super_admin", "admin", "centralised_team", "agent", "ops_lead", "front_desk", "housekeeping", "bde"] as const;

const CreateSchema = z.object({
  matchType: z.enum(TASK_TYPES),
  matchCategory: z.string().nullable().optional(),
  matchCondition: z.string().nullable().optional(),
  assignToRole: z.enum(ALL_ROLES),
  defaultPriority: z.enum(["P1", "P2", "P3", "P4"]),
});
const EditSchema = z.object({
  matchType: z.enum(TASK_TYPES),
  matchCategory: z.string().nullable().optional(),
  matchCondition: z.string().nullable().optional(),
  assignToRole: z.enum(ALL_ROLES),
  defaultPriority: z.enum(["P1", "P2", "P3", "P4"]),
  active: z.boolean(),
});
type CreateValues = z.infer<typeof CreateSchema>;
type EditValues = z.infer<typeof EditSchema>;

const EMPTY_CREATE: CreateValues = {
  matchType: "complaint",
  matchCategory: null,
  matchCondition: null,
  assignToRole: "front_desk",
  defaultPriority: "P3",
};

function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}

export default function TaskRoutingSettingsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "super_admin" || user?.role === "ops_lead";

  const queryClient = useQueryClient();
  // GET is open to any authenticated role server-side — `canEdit` below
  // gates whether this page renders the CRUD UI, not the fetch itself.
  // Already sorted sortOrder ascending server-side (TCFG-03) — that order
  // IS the first-match-wins evaluation order.
  const { data: rules, isLoading } = useListRoutingRules();

  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<RoutingRule | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<RoutingRule | null>(null);

  const createMutation = useCreateRoutingRule();
  const updateMutation = useUpdateRoutingRule();
  const archiveMutation = useDeleteRoutingRule();
  const reorderMutation = useReorderRoutingRule();
  const [reorderingId, setReorderingId] = useState<number | null>(null);

  const createForm = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({
    resolver: zodResolver(EditSchema),
    defaultValues: { matchType: "complaint", matchCategory: null, matchCondition: null, assignToRole: "front_desk", defaultPriority: "P3", active: true },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getListRoutingRulesQueryKey() });
  }
  function openEdit(r: RoutingRule) {
    setEditTarget(r);
    editForm.reset({
      matchType: r.matchType as CreateValues["matchType"],
      matchCategory: r.matchCategory ?? null,
      matchCondition: r.matchCondition ?? null,
      assignToRole: r.assignToRole as CreateValues["assignToRole"],
      defaultPriority: r.defaultPriority,
      active: r.active,
    });
  }
  function closeDialogs() {
    setOpenCreate(false);
    setEditTarget(null);
    createForm.reset(EMPTY_CREATE);
  }

  async function onCreate(values: CreateValues) {
    try {
      await createMutation.mutateAsync({ data: values });
      toast.success(`Created routing rule for "${values.matchType}".`);
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to add routing rules.")
          : "Could not create the routing rule.",
      );
    }
  }
  async function onEdit(values: EditValues) {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
      toast.success("Routing rule saved.");
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to edit routing rules.")
          : "Could not save the routing rule.",
      );
    }
  }
  async function onArchive() {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync({ id: archiveTarget.id });
      toast.success(`Archived routing rule for "${archiveTarget.matchType}".`);
      invalidate();
      setArchiveTarget(null);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to archive routing rules.")
          : "Could not archive the routing rule.",
      );
    }
  }
  async function move(id: number, direction: "up" | "down") {
    setReorderingId(id);
    try {
      await reorderMutation.mutateAsync({ id, data: { direction } });
      invalidate();
    } catch (err) {
      const status = errStatus(err);
      const data = errData(err);
      if (status === 422 && data?.error === "no_neighbor") {
        toast.error(direction === "up" ? "Already at the top." : "Already at the bottom.");
      } else if (status === 403) {
        toast.error((data?.message as string) ?? "You don't have permission to reorder routing rules.");
      } else {
        toast.error("Could not reorder the routing rule.");
      }
    } finally {
      setReorderingId(null);
    }
  }

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Restricted</CardTitle>
          <CardDescription>Routing rule settings are limited to admin and ops lead roles.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const sorted = rules ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Route className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Routing Rules</h1>
            <p className="text-sm text-muted-foreground">
              Ordered top-to-bottom, first match wins. Use the arrows to reorder — order persists immediately.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpenCreate(true)}>Add Routing Rule</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rules {sorted.length ? <span className="text-muted-foreground font-normal text-sm">({sorted.length})</span> : null}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px]">Order</TableHead>
                <TableHead>Match Type</TableHead>
                <TableHead>Match Category</TableHead>
                <TableHead>Assign To Role</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[160px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
                ))
              ) : sorted.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No routing rules yet.</TableCell></TableRow>
              ) : (
                sorted.map((r, idx) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground w-5 text-center">{idx + 1}</span>
                        <div className="flex flex-col">
                          <Button
                            size="icon" variant="ghost" className="h-5 w-5"
                            disabled={idx === 0 || reorderingId === r.id}
                            onClick={() => move(r.id, "up")}
                            aria-label="Move up"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon" variant="ghost" className="h-5 w-5"
                            disabled={idx === sorted.length - 1 || reorderingId === r.id}
                            onClick={() => move(r.id, "down")}
                            aria-label="Move down"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{r.matchType.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-sm">{r.matchCategory ?? "— (fallback)"}</TableCell>
                    <TableCell className="capitalize text-sm">{r.assignToRole.replace(/_/g, " ")}</TableCell>
                    <TableCell><Badge variant="secondary">{r.defaultPriority}</Badge></TableCell>
                    <TableCell><Badge variant={r.active ? "default" : "outline"}>{r.active ? "Active" : "Archived"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                        {r.active && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setArchiveTarget(r)}>
                            Archive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit dialog — two separate <Form> trees (create vs edit have
          different field sets, e.g. `active`; react-hook-form's generic
          Control<T> types aren't compatible across differently-shaped
          schemas, so a single shared <Form> can't switch its control prop). */}
      <Dialog open={openCreate || editTarget !== null} onOpenChange={(o) => !o && closeDialogs()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? "Edit Routing Rule" : "Add Routing Rule"}</DialogTitle>
            <DialogDescription>New rules append to the bottom of the order — reorder afterward with the arrows.</DialogDescription>
          </DialogHeader>
          {editTarget ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEdit)} className="space-y-3">
                <FormField name="matchType" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Type <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {TASK_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="matchCategory" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Category</FormLabel>
                    <FormControl>
                      <Input placeholder="Blank = wildcard/fallback for this type" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">Category name, or a comma-separated list. Leave blank for a catch-all fallback.</p>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="matchCondition" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Condition</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional — e.g. a zone or account filter" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="assignToRole" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To Role <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="defaultPriority" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Priority</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="active" control={editForm.control} render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Active</FormLabel>
                      <p className="text-xs text-muted-foreground">Off = archived; skipped during evaluation.</p>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving…" : "Save Rule"}</Button>
                </DialogFooter>
              </form>
            </Form>
          ) : (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-3">
                <FormField name="matchType" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Type <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {TASK_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="matchCategory" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Category</FormLabel>
                    <FormControl>
                      <Input placeholder="Blank = wildcard/fallback for this type" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">Category name, or a comma-separated list. Leave blank for a catch-all fallback.</p>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="matchCondition" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Match Condition</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional — e.g. a zone or account filter" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="assignToRole" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To Role <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="defaultPriority" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Priority</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Save Rule"}</Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this routing rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets it to inactive — it's skipped during first-match-wins evaluation, but stays visible here.
              Reversible from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} disabled={archiveMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
